/**
 * CategoryCombobox
 *
 * Searchable dropdown for selecting product categories.
 * Shows existing subcategories for the selected product type.
 * Falls back to "Create new" when the typed value doesn't match.
 */
import { useState, useEffect, useRef } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { getCategoriesForType } from "@/lib/productCategories";

export function CategoryCombobox({ value, onChange, productType, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const categories = getCategoriesForType(productType);
  const trimmed = search.trim().toLowerCase();

  const filtered = trimmed
    ? categories.filter(
        (c) =>
          c.label.toLowerCase().includes(trimmed) ||
          c.slug.toLowerCase().includes(trimmed)
      )
    : categories;

  const currentLabel =
    categories.find((c) => c.slug === value)?.label || value || "";

  // Has exact match for the typed value?
  const exactMatch = categories.some(
    (c) =>
      c.slug === trimmed ||
      c.label.toLowerCase() === trimmed
  );

  const showCreateOption = trimmed && !exactMatch && filtered.length === 0;

  const select = (slug) => {
    onChange(slug);
    setSearch("");
    setOpen(false);
  };

  const createNew = () => {
    const slug = trimmed.replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (slug) {
      onChange(slug);
      setSearch("");
      setOpen(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        listRef.current &&
        !listRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Empty state — no known categories for this product type
  if (categories.length === 0) {
    return (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. general"
        className="w-full bg-[#0a0a0a] border border-white/10 text-white text-xs rounded px-3 py-2.5 outline-none transition-colors hover:border-white/20 focus:border-[#CBA052]/40 focus:ring-1 focus:ring-[#CBA052]/20"
        data-testid={testId}
      />
    );
  }

  return (
    <div className="relative" data-testid={testId}>
      {/* Trigger */}
      <button
        ref={inputRef}
        type="button"
        onClick={() => { setOpen((v) => !v); setSearch(""); }}
        className="w-full flex items-center justify-between bg-[#0a0a0a] border border-white/10 text-xs rounded px-3 py-2.5 outline-none transition-colors hover:border-white/20 focus:border-[#CBA052]/40 focus:ring-1 focus:ring-[#CBA052]/20"
      >
        <span className={currentLabel ? "text-white" : "text-white/30"}>
          {currentLabel || "— Select category —"}
        </span>
        <ChevronsUpDown size={12} className="text-white/30 ml-2 shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={listRef}
          className="absolute z-50 top-full left-0 right-0 mt-1 border border-white/10 bg-[#0d0d0d] rounded shadow-xl max-h-60 overflow-hidden"
          data-testid={`${testId}-dropdown`}
        >
          {/* Search input */}
          <div className="p-2 border-b border-white/[0.06]">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered.length === 1) {
                  e.preventDefault();
                  select(filtered[0].slug);
                } else if (e.key === "Enter" && showCreateOption) {
                  e.preventDefault();
                  createNew();
                } else if (e.key === "Escape") {
                  setOpen(false);
                  setSearch("");
                }
              }}
              placeholder="Search categories..."
              className="w-full bg-transparent border-none text-white text-xs outline-none placeholder:text-white/20"
              data-testid={`${testId}-search`}
            />
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-48 p-1">
            {filtered.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => select(c.slug)}
                className={`w-full flex items-center gap-2 text-left text-xs px-2.5 py-2 rounded transition-colors ${
                  value === c.slug
                    ? "bg-[#CBA052]/10 text-[#CBA052]"
                    : "text-white/70 hover:bg-white/[0.04] hover:text-white"
                }`}
                data-testid={`${testId}-option-${c.slug}`}
              >
                <Check
                  size={12}
                  className={value === c.slug ? "text-[#CBA052]" : "text-transparent"}
                />
                <span>{c.label}</span>
                <span className="ml-auto text-[9px] text-white/20 font-mono">{c.slug}</span>
              </button>
            ))}

            {/* "Create new" fallback */}
            {showCreateOption && (
              <button
                type="button"
                onClick={createNew}
                className="w-full flex items-center gap-2 text-left text-xs px-2.5 py-2 rounded text-[#CBA052]/70 hover:bg-[#CBA052]/[0.06] hover:text-[#CBA052] transition-colors"
                data-testid={`${testId}-create-new`}
              >
                <Plus size={12} />
                <span>Create "{search.trim()}"</span>
              </button>
            )}

            {filtered.length === 0 && !showCreateOption && (
              <p className="text-[10px] text-white/20 text-center py-3">No matching categories</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
